import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";

function suggestCode(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_")
    .slice(0, 32);
}

const EMPTY_FORM = {
  name: "",
  name_en: "",
  code: "",
  tourist_abbreviation: "",
  sort_order: "0",
  requires_vehicle_length: false,
  online_booking_enabled: true,
};

export interface CreateArrivalTypeModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  touristIntegrationEnabled?: boolean;
}

export function CreateArrivalTypeModal({
  open,
  onClose,
  onCreated,
  touristIntegrationEnabled = false,
}: CreateArrivalTypeModalProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [codeTouched, setCodeTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setCodeTouched(false);
    setError(null);
    setSubmitting(false);
  }, [open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/v1/arrival-types", {
        name: form.name.trim(),
        name_en: form.name_en.trim() || null,
        code: form.code.trim().toLowerCase(),
        tourist_abbreviation: touristIntegrationEnabled
          ? form.tourist_abbreviation.trim() || null
          : null,
        sort_order: Number(form.sort_order) || 0,
        requires_vehicle_length: form.requires_vehicle_length,
        online_booking_enabled: form.online_booking_enabled,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neue Anreiseart"
      description="Bezeichnung und Code für Rezeption und Online-Buchung"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <label className="flex flex-col gap-1 text-sm">
          Bezeichnung (Deutsch)
          <input
            required
            autoFocus
            className="eh-input"
            value={form.name}
            onChange={(e) => {
              const name = e.target.value;
              setForm((f) => ({
                ...f,
                name,
                code: codeTouched ? f.code : suggestCode(name),
              }));
            }}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Bezeichnung (Englisch)
          <input
            className="eh-input"
            value={form.name_en}
            onChange={(e) => setForm((f) => ({ ...f, name_en: e.target.value }))}
            placeholder="Optional für englische Buchungsstrecke"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Code (technisch, nicht änderbar)
          <input
            required
            pattern="[a-z][a-z0-9_]*"
            title="Kleinbuchstaben, Ziffern und Unterstrich"
            className="eh-input font-mono"
            value={form.code}
            onChange={(e) => {
              setCodeTouched(true);
              setForm((f) => ({ ...f, code: e.target.value.toLowerCase() }));
            }}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Sortierung
          <input
            type="number"
            className="eh-input w-32"
            value={form.sort_order}
            onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
          />
        </label>
        {touristIntegrationEnabled ? (
          <label className="flex flex-col gap-1 text-sm">
            Tourist-Kürzel (PMS carnumber)
            <input
              className="eh-input font-mono uppercase"
              value={form.tourist_abbreviation}
              onChange={(e) =>
                setForm((f) => ({ ...f, tourist_abbreviation: e.target.value }))
              }
              placeholder="z. B. WW, MC, ZELT"
              maxLength={32}
            />
          </label>
        ) : null}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.requires_vehicle_length}
            onChange={(e) =>
              setForm((f) => ({ ...f, requires_vehicle_length: e.target.checked }))
            }
          />
          Fahrzeuglänge erforderlich
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.online_booking_enabled}
            onChange={(e) =>
              setForm((f) => ({ ...f, online_booking_enabled: e.target.checked }))
            }
          />
          In Online-Buchung wählbar
        </label>
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" loading={submitting}>
            Anlegen
          </Button>
        </div>
      </form>
    </Modal>
  );
}
